import React from 'react'
import { AppService } from '../../services/index'
import { Container } from 'typescript-ioc'
import { Drawer, executeCleanDrawerRender } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../_utils/classnames'
import { Form } from 'antd'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { SecretsModel } from '../../models/domain/secrets.model'
import { DomainSecretsEditor } from './domain-secrets-editor'
import { simpleNotification } from '../../_utils/utils'
import { DomainSecret } from '@pushly/models/lib/structs/domain-secrets/domain-secret'
import { DomainSecretType } from '@pushly/models/lib/enums/domain-secret-type'

const ALIAS_REGEX = /^[a-z0-9_][a-z0-9_-]{1,63}$/i
const INVALID_ALIAS_MESSAGE =
    'Alias can include letters, numbers, underscores, and hyphens only. It must start with a letter, number, or underscore.'

interface IDomainSecretsEditorProps {
    visible: boolean
    onClose: () => void
    domainId: number
    secret?: DomainSecret | undefined
    onSubmit: (value: any) => any
    submitDisabled?: (value: any) => boolean
}

interface IDomainSecretsEditorState {
    tmpValue: SecretsModel
    lastSecretKey?: string
}

export class DomainSecretsDrawer extends React.Component<IDomainSecretsEditorProps, IDomainSecretsEditorState> {
    public static getDerivedStateFromProps(
        nextProps: Readonly<IDomainSecretsEditorProps>,
        prevState: Readonly<IDomainSecretsEditorState>,
    ): Partial<IDomainSecretsEditorState> | null {
        const nextKey = DomainSecretsDrawer.prototype.buildSecretKey(nextProps.secret)

        if (nextKey !== prevState.lastSecretKey) {
            return {
                tmpValue: DomainSecretsDrawer.prototype.toSecretsModel(nextProps.secret),
                lastSecretKey: nextKey,
            }
        }

        return null
    }

    protected appSvc: AppService

    public constructor(props: IDomainSecretsEditorProps) {
        super(props)

        this.state = {
            tmpValue: this.toSecretsModel(this.props.secret),
            lastSecretKey: this.buildSecretKey(this.props.secret),
        }

        this.appSvc = Container.get(AppService)

        executeCleanDrawerRender(this)
    }

    public render() {
        const isEditMode = !!this.props.secret
        const editorKey = this.props.secret
            ? `edit-${this.props.secret.id ?? this.props.secret.alias ?? 'secret'}`
            : 'new-secret'

        return (
            <Drawer
                getContainer={this.appSvc.getAppContainer}
                className={getClassNames('ddf-integrations-editor')}
                title={isEditMode ? 'Update secret' : 'Create secret'}
                onClose={this.handleClose}
                placement="right"
                closable={true}
                destroyOnHidden={true}
                open={this.props.visible}
                disableSubmit={this.props.submitDisabled!(this.state.tmpValue)}
                onSubmit={this.handleSubmit}
                width={800}
            >
                <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} labelAlign="left">
                    <DomainSecretsEditor
                        key={editorKey}
                        secret={this.state.tmpValue}
                        isEditMode={isEditMode}
                        domainId={this.props.domainId}
                        onChange={(value) => this.setState({ tmpValue: value })}
                        className={getClassNames('domain-secrets-editor')}
                    />
                </Form>
            </Drawer>
        )
    }

    protected toSecretsModel(secret?: DomainSecret): SecretsModel {
        const model = new SecretsModel()
        if (!secret) {
            return model
        }

        model.alias = secret.alias
        const type = secret.type as DomainSecretType
        model.type = Object.values(DomainSecretType).includes(type) ? type : DomainSecretType.TOKEN
        model.value = ''

        return model
    }

    protected buildSecretKey(secret?: DomainSecret) {
        if (!secret) {
            return undefined
        }

        return `${secret.id ?? ''}-${secret.alias ?? ''}-${secret.type ?? ''}-${(secret as any).value ?? ''}`
    }

    protected validate(secret: SecretsModel): { ok: boolean; error?: any } {
        const alias = secret.alias?.trim()

        if (!alias) {
            return { ok: false, error: 'Alias is required.' }
        }

        if (!ALIAS_REGEX.test(alias)) {
            return { ok: false, error: INVALID_ALIAS_MESSAGE }
        }

        const value = secret.value ?? ''

        const validators: Record<DomainSecretType, () => { ok: boolean; error?: string }> = {
            [DomainSecretType.JSON]: () => this.validateJsonSecret(value, secret),
            [DomainSecretType.TOKEN]: () => this.validateTokenSecret(value),
            [DomainSecretType.USERPASS]: () => this.validateUserPassSecret(value),
        }

        return validators[secret.type]()
    }

    protected handleSubmit = async () => {
        const secret = this.state.tmpValue
        if (!secret) {
            return
        }

        const res = this.validate(secret)
        if (!res.ok) {
            simpleNotification('error', res.error ?? 'Validation failed')
        } else {
            const submitRes = await this.props.onSubmit?.(secret)
            if (submitRes?.ok) {
                this.setState({ tmpValue: new SecretsModel() })
                return this.handleClose()
            }
        }
    }

    protected handleClose = () => {
        this.setState({
            tmpValue: new SecretsModel(),
            lastSecretKey: undefined,
        })
        this.props.onClose()
    }

    private validateJsonSecret(value: string, secret: SecretsModel) {
        if (!value.trim()) {
            return { ok: false, error: 'JSON object is not valid.' }
        }

        try {
            const parsed = JSON.parse(value)

            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                secret.value = JSON.stringify(parsed)
            } else {
                return { ok: false, error: 'JSON object is not valid.' }
            }
        } catch (err) {
            return { ok: false, error: `JSON object is not valid. ${err.message}` }
        }

        return { ok: true }
    }

    private validateTokenSecret(value: string) {
        if (value.trim().length === 0) {
            return { ok: false, error: 'Token value is required.' }
        }
        return { ok: true }
    }

    private validateUserPassSecret(value: string) {
        try {
            const parsed = JSON.parse(value || '{}')
            const isObject = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            const username = isObject && typeof parsed.username === 'string' ? parsed.username.trim() : ''
            const password = isObject && typeof parsed.password === 'string' ? parsed.password.trim() : ''

            if (!username) {
                return { ok: false, error: 'Username is required.' }
            }

            if (!password) {
                return { ok: false, error: 'Password is required.' }
            }
        } catch (err) {
            return { ok: false, error: 'Username and password are not valid.' }
        }

        return { ok: true }
    }
}
